/**
 * Danh sách án (nội bộ) – Thẩm phán/Thư ký/Chánh án: bảng đủ cột, KPI, search/filter/sort, pagination.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Visibility,
  FolderOff,
  Search,
  Download,
  Event,
  Description,
  FolderOpen,
  ChevronRight,
} from '@mui/icons-material'
import { PageDataState } from '../../components/PageDataState'
import { EmptyState } from '../../components/EmptyState'
import { useGetCasesQuery } from '../../store/api/casesApi'
import type { CaseStatus, CaseTrack } from '../../types/case'

const statusLabels: Record<CaseStatus, string> = {
  draft: 'Nháp',
  filed: 'Đã nộp',
  accepted: 'Đã thụ lý',
  pre_trial: 'Chuẩn bị xét xử',
  trial: 'Đang xét xử',
  closed: 'Đã kết thúc',
  returned: 'Trả đơn',
  transferred: 'Chuyển đơn',
}

/** Màu badge trạng thái phân biệt rõ (không trùng tone). */
const statusBadge: Record<CaseStatus, { bg: string; color: string }> = {
  draft: { bg: '#9e9e9e', color: '#fff' },
  filed: { bg: '#1976d2', color: '#fff' },
  accepted: { bg: '#2e7d32', color: '#fff' },
  pre_trial: { bg: '#ed6c02', color: '#fff' },
  trial: { bg: '#9c27b0', color: '#fff' },
  closed: { bg: '#2e7d32', color: '#fff' },
  returned: { bg: '#d32f2f', color: '#fff' },
  transferred: { bg: '#ed6c02', color: '#fff' },
}

const trackLabels: Record<CaseTrack, string> = {
  A: 'Online',
  B: 'Hybrid',
  C: 'Trực tiếp',
}

const categoryLabels: Record<string, string> = {
  marriage_family: 'Hôn nhân và gia đình',
  civil: 'Dân sự',
  land: 'Đất đai',
  business: 'Kinh doanh thương mại',
}

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50]

function formatClaimValue(vnd: number): string {
  if (vnd >= 1e9) return `${(vnd / 1e9).toFixed(1)} tỷ`
  return `${(vnd / 1e6).toFixed(0)} triệu`
}

function formatDate(iso?: string): string {
  if (!iso) return '–'
  return new Date(iso).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function daysSince(iso: string): number {
  const d = new Date(iso)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  d.setHours(0, 0, 0, 0)
  return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24))
}

export default function WorkspaceCasesPage() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<CaseStatus | 'all'>('all')
  const [trackFilter, setTrackFilter] = useState<CaseTrack | 'all'>('all')
  const [quickFilter, setQuickFilter] = useState<'all' | 'deadline' | 'pending'>('all')
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<'filed_at' | 'deadline_at' | 'case_code'>('filed_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const { data: cases = [], isLoading, error, refetch } = useGetCasesQuery()

  const filtered = useMemo(() => {
    let list = [...cases]
    if (statusFilter !== 'all') list = list.filter((c) => c.status === statusFilter)
    if (trackFilter !== 'all') list = list.filter((c) => c.track === trackFilter)
    if (quickFilter === 'deadline') {
      const now = new Date()
      list = list.filter((c) => c.deadline_at && new Date(c.deadline_at) <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000))
    }
    if (quickFilter === 'pending') list = list.filter((c) => ['filed', 'accepted', 'pre_trial'].includes(c.status))
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(
        (c) =>
          c.case_code.toLowerCase().includes(q) ||
          (c.plaintiff_name || '').toLowerCase().includes(q) ||
          (c.defendant_name || '').toLowerCase().includes(q)
      )
    }
    list.sort((a, b) => {
      let va: string | number = 0
      let vb: string | number = 0
      if (sortBy === 'filed_at') {
        va = a.filed_at ? new Date(a.filed_at).getTime() : 0
        vb = b.filed_at ? new Date(b.filed_at).getTime() : 0
      } else if (sortBy === 'deadline_at') {
        va = a.deadline_at ? new Date(a.deadline_at).getTime() : 9999999999999
        vb = b.deadline_at ? new Date(b.deadline_at).getTime() : 9999999999999
      } else {
        va = a.case_code
        vb = b.case_code
      }
      if (typeof va === 'number' && typeof vb === 'number') return sortOrder === 'asc' ? va - vb : vb - va
      return sortOrder === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va))
    })
    return list
  }, [cases, statusFilter, trackFilter, quickFilter, search, sortBy, sortOrder])

  const paginated = useMemo(() => {
    const start = page * rowsPerPage
    return filtered.slice(start, start + rowsPerPage)
  }, [filtered, page, rowsPerPage])

  const kpi = useMemo(() => {
    const total = cases.length
    const inProgress = cases.filter((c) => ['filed', 'accepted', 'pre_trial', 'trial'].includes(c.status)).length
    const now = new Date()
    const in7Days = now.getTime() + 7 * 24 * 60 * 60 * 1000
    const nearDeadline = cases.filter((c) => c.deadline_at && new Date(c.deadline_at).getTime() <= in7Days && !['closed'].includes(c.status)).length
    const closed = cases.filter((c) => c.status === 'closed').length
    return { total, inProgress, nearDeadline, closed }
  }, [cases])

  const handleSort = (field: 'filed_at' | 'deadline_at' | 'case_code') => {
    if (sortBy === field) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else setSortBy(field)
  }

  const renderHearingOrDeadline = (c: typeof cases[0]) => {
    if (c.next_hearing_at) return { text: `Phiên: ${formatDate(c.next_hearing_at)}`, isHearing: true }
    if (c.deadline_at) return { text: `Hạn: ${formatDate(c.deadline_at)}`, isHearing: false }
    return null
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ mb: 0.5 }}>
        <Link underline="hover" color="inherit" href="/workspace" onClick={(e) => { e.preventDefault(); navigate('/workspace') }}>
          Bảng điều khiển
        </Link>
        <Typography color="text.primary">Danh sách án</Typography>
      </Breadcrumbs>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
            Danh sách án
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Xem và xử lý vụ án · Phân công Thẩm phán · Theo dõi tiến độ
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<Download />} sx={{ flexShrink: 0 }}>
          Xuất Excel
        </Button>
      </Box>

      {/* KPI strip – lấp không gian, có ý nghĩa */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Card variant="outlined" sx={{ flex: '1 1 140px', minWidth: 0 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Tổng vụ án</Typography>
            <Typography variant="h5" fontWeight={700}>{kpi.total}</Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: '1 1 140px', minWidth: 0 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Đang xử lý</Typography>
            <Typography variant="h5" fontWeight={700} color="primary.main">{kpi.inProgress}</Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: '1 1 140px', minWidth: 0 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Gần deadline (7 ngày)</Typography>
            <Typography variant="h5" fontWeight={700} color="warning.main">{kpi.nearDeadline}</Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: '1 1 140px', minWidth: 0 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Đã kết thúc</Typography>
            <Typography variant="h5" fontWeight={700} color="success.main">{kpi.closed}</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search, filter, sort */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Tìm mã vụ án, tên đương sự..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Trạng thái</InputLabel>
          <Select value={statusFilter} label="Trạng thái" onChange={(e) => setStatusFilter(e.target.value as CaseStatus | 'all')}>
            <MenuItem value="all">Tất cả</MenuItem>
            {(Object.keys(statusLabels) as CaseStatus[]).map((s) => (
              <MenuItem key={s} value={s}>{statusLabels[s]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Phân luồng</InputLabel>
          <Select value={trackFilter} label="Phân luồng" onChange={(e) => setTrackFilter(e.target.value as CaseTrack | 'all')}>
            <MenuItem value="all">Tất cả</MenuItem>
            <MenuItem value="A">Online</MenuItem>
            <MenuItem value="B">Hybrid</MenuItem>
            <MenuItem value="C">Trực tiếp</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Lọc nhanh</InputLabel>
          <Select value={quickFilter} label="Lọc nhanh" onChange={(e) => setQuickFilter(e.target.value as typeof quickFilter)}>
            <MenuItem value="all">Tất cả</MenuItem>
            <MenuItem value="deadline">Gần deadline</MenuItem>
            <MenuItem value="pending">Đang chờ xử lý</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Sắp xếp: <strong>{sortBy === 'filed_at' ? 'Ngày thụ lý' : sortBy === 'deadline_at' ? 'Hạn xử lý' : 'Mã vụ án'}</strong> ({sortOrder === 'asc' ? 'A→Z' : 'Z→A'})
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 2, boxShadow: 1, width: '100%' }}>
        <Tabs value={statusFilter} onChange={(_, v) => setStatusFilter(v)} sx={{ px: 2, pt: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Tất cả" value="all" />
          <Tab label="Đã nộp" value="filed" />
          <Tab label="Đã thụ lý" value="accepted" />
          <Tab label="Chuẩn bị xét xử" value="pre_trial" />
          <Tab label="Đang xét xử" value="trial" />
          <Tab label="Đã kết thúc" value="closed" />
        </Tabs>
        <PageDataState
          isLoading={isLoading}
          error={error}
          empty={filtered.length === 0}
          emptyContent={
            <EmptyState
              icon={<FolderOff sx={{ fontSize: 40 }} />}
              title="Không có vụ án nào phù hợp"
              description="Thử đổi bộ lọc hoặc từ khóa tìm kiếm."
              actionLabel="Xóa bộ lọc"
              onAction={() => { setStatusFilter('all'); setTrackFilter('all'); setQuickFilter('all'); setSearch('') }}
            />
          }
          onRetry={() => refetch()}
        >
          <>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
              {paginated.map((c) => {
                const hd = renderHearingOrDeadline(c)
                return (
                  <Card key={c.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>{c.case_code}</Typography>
                        <Chip label={statusLabels[c.status]} size="small" sx={{ bgcolor: statusBadge[c.status].bg, color: statusBadge[c.status].color, border: 'none', fontSize: '0.7rem' }} />
                      </Box>
                      <Typography variant="body2">{c.plaintiff_name || c.plaintiff_id} / {c.defendant_name || c.defendant_id}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {categoryLabels[c.category] ?? c.category} · {trackLabels[c.track]} · {formatClaimValue(c.claim_value_vnd)}
                      </Typography>
                      {hd && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{hd.text}</Typography>}
                      {(c.assigned_judge_name || c.secretary_name) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {c.assigned_judge_name && `TP: ${c.assigned_judge_name}`}
                          {c.assigned_judge_name && c.secretary_name && ' · '}
                          {c.secretary_name && `TK: ${c.secretary_name}`}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                        <Button size="small" startIcon={<Visibility />} onClick={() => navigate(`/workspace/cases/${c.id}`)}>Chi tiết</Button>
                        <IconButton size="small" onClick={() => navigate('/workspace/calendar')}><Event fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => navigate('/workspace/ai-draft')}><Description fontSize="small" /></IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                )
              })}
            </Box>
          ) : (
          <TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Mã vụ án</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Nguyên đơn / Bị đơn</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Loại vụ</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Phân luồng</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Trạng thái</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Giá trị tranh chấp</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Phiên / Hạn</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Ngày từ thụ lý</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Thẩm phán</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Thư ký</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((c) => {
                  const hd = renderHearingOrDeadline(c)
                  return (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{c.case_code}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{c.plaintiff_name || c.plaintiff_id}</Typography>
                      <Typography variant="caption" color="text.secondary">/ {c.defendant_name || c.defendant_id}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>{categoryLabels[c.category] ?? c.category}</TableCell>
                    <TableCell>
                      <Chip label={trackLabels[c.track]} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[c.status]}
                        size="small"
                        sx={{ bgcolor: statusBadge[c.status].bg, color: statusBadge[c.status].color, border: 'none', fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.875rem' }}>{formatClaimValue(c.claim_value_vnd)}</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                      {hd ? hd.text : '–'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.875rem' }}>{c.filed_at ? daysSince(c.filed_at) : '–'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>{c.assigned_judge_name ? c.assigned_judge_name.replace('Thẩm phán ', '') : '–'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>{c.secretary_name ? c.secretary_name.replace('Thư ký ', '') : '–'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Chi tiết vụ án">
                        <IconButton size="small" onClick={() => navigate(`/workspace/cases/${c.id}`)}><Visibility fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Lịch phiên">
                        <IconButton size="small" onClick={() => navigate('/workspace/calendar')}><Event fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Soạn thảo văn bản">
                        <IconButton size="small" onClick={() => navigate('/workspace/ai-draft')}><Description fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Chứng cứ">
                        <IconButton size="small" onClick={() => navigate('/workspace/ai-evidence')}><FolderOpen fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          {filtered.length > 0 && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Số dòng/trang"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
          />
          )}
          </>
        </PageDataState>
      </Card>
    </Box>
  )
}
